import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Info,
  DollarSign,
  Layers,
  TrendingUp,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
} from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { listarClientes } from '../../clientes/api';
import { listarUsuarios } from '../../usuarios/api';
import { useDominio } from '../hooks';
import type {
  AddItemPayload,
  AddServicoPayload,
  BaseComercial,
  BaseComercialItem,
  BaseComercialServico,
  CreateBaseComercialPayload,
  MoedaBaseComercial,
  ReajustePeriodicidade,
  SituacaoBaseComercial,
  UpdateBaseComercialPayload,
} from '../api';

/** Periodicidade de reajuste (RN-COM-013). */
const REAJUSTE_PERIODICIDADE_OPTIONS: { value: ReajustePeriodicidade; label: string }[] = [
  { value: 'nenhuma', label: 'Sem reajuste' },
  { value: 'mensal', label: 'Mensal' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
  { value: 'indice', label: 'Por índice' },
];

const MOEDA_OPTIONS: { value: MoedaBaseComercial; label: string }[] = [
  { value: 'BRL', label: 'Real (BRL)' },
  { value: 'USD', label: 'Dólar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
];

export interface BaseComercialFormState {
  nome: string;
  clientId: string;
  responsavelComercialId: string;
  dataInicio: string;
  dataFim: string;
  modalidade: string;
  escopo: string;
  moeda: MoedaBaseComercial;
  descontoMaximoPercentual: string;
  reajustePeriodicidade: ReajustePeriodicidade;
  reajusteIndexSlug: string;
  reajustePercentualFixo: string;
  permiteCoexistencia: boolean;
  requerAprovacaoEletronica: boolean;
  condicoesFaturamento: string;
  observacoesTributarias: string;
  observacoes: string;
  /** Serviços e itens (estrutura) — task 10.4. */
  servicos: Array<{
    localId: string;
    serviceTypeSlug: string;
    descricao: string;
    itens: Array<{
      localId: string;
      professionalProfileSlug: string;
      pricingModelSlug: string;
      quantidade: string;
      valorUnitario: string;
      custoUnitario: string;
      vigenciaInicio: string;
      vigenciaFim: string;
      observacoes: string;
    }>;
  }>;
}

const EMPTY_STATE: BaseComercialFormState = {
  nome: '',
  clientId: '',
  responsavelComercialId: '',
  dataInicio: '',
  dataFim: '',
  modalidade: '',
  escopo: '',
  moeda: 'BRL',
  descontoMaximoPercentual: '',
  reajustePeriodicidade: 'nenhuma',
  reajusteIndexSlug: '',
  reajustePercentualFixo: '',
  permiteCoexistencia: false,
  requerAprovacaoEletronica: false,
  condicoesFaturamento: '',
  observacoesTributarias: '',
  observacoes: '',
  servicos: [],
};

function makeLocalId(): string {
  return `local-${Math.random().toString(36).slice(2, 10)}`;
}

function itemToState(i: BaseComercialItem): BaseComercialFormState['servicos'][number]['itens'][number] {
  return {
    localId: i.id,
    professionalProfileSlug: i.professionalProfileSlug,
    pricingModelSlug: i.pricingModelSlug,
    quantidade: i.quantidade ?? '1',
    valorUnitario: i.valorUnitario ?? '',
    custoUnitario: i.custoUnitario ?? '',
    vigenciaInicio: i.vigenciaInicio ? i.vigenciaInicio.slice(0, 10) : '',
    vigenciaFim: i.vigenciaFim ? i.vigenciaFim.slice(0, 10) : '',
    observacoes: i.observacoes ?? '',
  };
}

/**
 * Monta o estado do form a partir de uma Base já persistida (edição).
 * Os itens chegam por `id` de servico — o estado local usa localId para
 * agrupar itens sob seu serviço na edição da estrutura.
 */
export function stateFromBaseComercial(
  b: BaseComercial,
  servicos: BaseComercialServico[],
  itens: BaseComercialItem[],
): BaseComercialFormState {
  const servicosEstado = servicos.map((s) => ({
    localId: s.id,
    serviceTypeSlug: s.serviceTypeSlug,
    descricao: s.descricao ?? '',
    itens: itens
      .filter((i) => i.commercialBaseServiceId === s.id)
      .map(itemToState),
  }));
  return {
    ...EMPTY_STATE,
    nome: b.nome,
    clientId: b.clientId,
    responsavelComercialId: b.responsavelComercialId,
    dataInicio: b.dataInicio ? b.dataInicio.slice(0, 10) : '',
    dataFim: b.dataFim ? b.dataFim.slice(0, 10) : '',
    modalidade: b.modalidade,
    escopo: b.escopo ?? '',
    moeda: b.moeda,
    descontoMaximoPercentual: b.descontoMaximoPercentual ?? '',
    reajustePeriodicidade: b.reajustePeriodicidade,
    reajusteIndexSlug: b.reajusteIndexSlug ?? '',
    reajustePercentualFixo: b.reajustePercentualFixo ?? '',
    permiteCoexistencia: b.permiteCoexistencia,
    requerAprovacaoEletronica: b.requerAprovacaoEletronica,
    condicoesFaturamento: b.condicoesFaturamento ?? '',
    observacoesTributarias: b.observacoesTributarias ?? '',
    observacoes: b.observacoes ?? '',
    servicos: servicosEstado,
  };
}

export function buildCreatePayload(
  state: BaseComercialFormState,
): CreateBaseComercialPayload {
  return {
    nome: state.nome.trim(),
    clientId: state.clientId,
    responsavelComercialId: state.responsavelComercialId,
    dataInicio: state.dataInicio,
    dataFim: state.dataFim || null,
    modalidade: state.modalidade.trim(),
    escopo: state.escopo.trim() || undefined,
    moeda: state.moeda,
    descontoMaximoPercentual: state.descontoMaximoPercentual
      ? Number(state.descontoMaximoPercentual)
      : null,
    reajustePeriodicidade: state.reajustePeriodicidade,
    reajusteIndexSlug: state.reajusteIndexSlug || null,
    reajustePercentualFixo: state.reajustePercentualFixo
      ? Number(state.reajustePercentualFixo)
      : undefined,
    permiteCoexistencia: state.permiteCoexistencia,
    requerAprovacaoEletronica: state.requerAprovacaoEletronica,
    condicoesFaturamento: state.condicoesFaturamento.trim() || undefined,
    observacoesTributarias: state.observacoesTributarias.trim() || undefined,
    observacoes: state.observacoes.trim() || undefined,
  };
}

export function buildUpdatePayload(
  state: BaseComercialFormState,
  original: BaseComercial,
): UpdateBaseComercialPayload {
  const p: UpdateBaseComercialPayload = {};
  if (state.nome.trim() !== original.nome) p.nome = state.nome.trim();
  if (state.responsavelComercialId !== original.responsavelComercialId)
    p.responsavelComercialId = state.responsavelComercialId;
  const di = state.dataInicio;
  if (di !== (original.dataInicio ? original.dataInicio.slice(0, 10) : ''))
    p.dataInicio = di;
  const df = state.dataFim || null;
  if (df !== (original.dataFim ? original.dataFim.slice(0, 10) : null)) p.dataFim = df;
  if (state.modalidade.trim() !== original.modalidade)
    p.modalidade = state.modalidade.trim();
  const esc = state.escopo.trim() || undefined;
  if (esc !== (original.escopo ?? undefined)) p.escopo = esc;
  if (state.moeda !== original.moeda) p.moeda = state.moeda;
  const desc = state.descontoMaximoPercentual
    ? Number(state.descontoMaximoPercentual)
    : null;
  const originalDesc =
    original.descontoMaximoPercentual != null
      ? Number(original.descontoMaximoPercentual)
      : null;
  if (desc !== originalDesc) p.descontoMaximoPercentual = desc;
  if (state.reajustePeriodicidade !== original.reajustePeriodicidade)
    p.reajustePeriodicidade = state.reajustePeriodicidade;
  const idx = state.reajusteIndexSlug || null;
  if (idx !== (original.reajusteIndexSlug ?? null)) p.reajusteIndexSlug = idx;
  const pct = state.reajustePercentualFixo
    ? Number(state.reajustePercentualFixo)
    : undefined;
  const originalPct =
    original.reajustePercentualFixo != null
      ? Number(original.reajustePercentualFixo)
      : undefined;
  if (pct !== originalPct) p.reajustePercentualFixo = pct;
  if (state.permiteCoexistencia !== original.permiteCoexistencia)
    p.permiteCoexistencia = state.permiteCoexistencia;
  if (state.requerAprovacaoEletronica !== original.requerAprovacaoEletronica)
    p.requerAprovacaoEletronica = state.requerAprovacaoEletronica;
  const cf = state.condicoesFaturamento.trim() || undefined;
  if (cf !== (original.condicoesFaturamento ?? undefined))
    p.condicoesFaturamento = cf;
  const ot = state.observacoesTributarias.trim() || undefined;
  if (ot !== (original.observacoesTributarias ?? undefined))
    p.observacoesTributarias = ot;
  const obs = state.observacoes.trim() || undefined;
  if (obs !== (original.observacoes ?? undefined)) p.observacoes = obs;
  return p;
}

/**
 * Valida os campos de identificação/precificação. A estrutura é
 * validada separadamente (`validateStructure`).
 */
export function validateForm(state: BaseComercialFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!state.nome.trim()) errors.nome = 'Nome da Base é obrigatório.';
  if (!state.clientId) errors.clientId = 'Cliente é obrigatório.';
  if (!state.responsavelComercialId)
    errors.responsavelComercialId = 'Responsável comercial é obrigatório.';
  if (!state.dataInicio) errors.dataInicio = 'Data de início é obrigatória.';
  if (!state.modalidade.trim()) errors.modalidade = 'Modalidade é obrigatória.';
  if (state.dataFim && state.dataInicio && state.dataFim < state.dataInicio) {
    errors.dataFim = 'Data final não pode ser anterior à data de início.';
  }
  if (state.descontoMaximoPercentual) {
    const n = Number(state.descontoMaximoPercentual);
    if (!Number.isFinite(n) || n < 0) {
      errors.descontoMaximoPercentual = 'Desconto máximo deve ser >= 0.';
    }
  }
  if (
    (state.reajustePeriodicidade === 'anual' ||
      state.reajustePeriodicidade === 'semestral' ||
      state.reajustePeriodicidade === 'mensal' ||
      state.reajustePeriodicidade === 'indice') &&
    !state.reajusteIndexSlug
  ) {
    errors.reajusteIndexSlug = 'Selecione o índice do reajuste.';
  }
  return errors;
}

/**
 * Valida os campos de UMA aba específica — usado pelo wizard "Continuar"
 * para bloquear o avanço enquanto a aba corrente tiver pendências. As
 * regras aqui são o subset das mesmas checagens de `validateForm` +
 * `validateStructure`, particionadas por aba.
 */
export function validateTabErrors(
  tab: 'identificacao' | 'estrutura' | 'precificacao' | 'reajuste',
  state: BaseComercialFormState,
): Record<string, string> {
  const all = { ...validateForm(state), ...validateStructure(state) };
  const KEYS_BY_TAB: Record<string, string[]> = {
    identificacao: [
      'nome',
      'clientId',
      'responsavelComercialId',
      'dataInicio',
      'dataFim',
      'modalidade',
    ],
    estrutura: ['structure'],
    precificacao: [],
    reajuste: ['reajusteIndexSlug', 'descontoMaximoPercentual'],
  };
  const keys = KEYS_BY_TAB[tab] ?? [];
  const result: Record<string, string> = {};
  for (const k of keys) if (all[k]) result[k] = all[k];
  return result;
}

/** Valida a estrutura serviço → perfil → precificação (task 10.4). */
export function validateStructure(state: BaseComercialFormState): Record<string, string> {
  const errors: Record<string, string> = {};
  if (state.servicos.length === 0) {
    errors.structure = 'Adicione ao menos um serviço com um perfil para prosseguir.';
    return errors;
  }
  for (const s of state.servicos) {
    if (!s.serviceTypeSlug) {
      errors.structure = 'Todos os serviços precisam de um tipo.';
      return errors;
    }
    if (s.itens.length === 0) {
      errors.structure = `O serviço "${s.serviceTypeSlug}" precisa de ao menos um perfil.`;
      return errors;
    }
    for (const it of s.itens) {
      if (!it.professionalProfileSlug || !it.pricingModelSlug) {
        errors.structure = 'Cada perfil precisa de forma de precificação.';
        return errors;
      }
      const v = Number(it.valorUnitario);
      if (!it.valorUnitario || !Number.isFinite(v) || v < 0) {
        errors.structure = 'Valor unitário é obrigatório e deve ser >= 0.';
        return errors;
      }
      if (it.vigenciaFim && it.vigenciaInicio && it.vigenciaFim < it.vigenciaInicio) {
        errors.structure = 'Vigência própria inválida (fim anterior ao início).';
        return errors;
      }
    }
  }
  return errors;
}

// ---------------------------------------------------------------------

type TabId = 'identificacao' | 'estrutura' | 'precificacao' | 'reajuste';

export interface BaseComercialFormProps {
  initialState?: BaseComercialFormState;
  onSubmit: (state: BaseComercialFormState) => Promise<void> | void;
  onCancel: () => void;
  submitLabel: string;
  isSubmitting?: boolean;
  submitError?: string | null;
  /** Nova Base: cria os itens junto. Edição publicada gera versão nova
      no backend (RN-COM-005) — a estrutura é gerenciada por endpoints
      próprios de item/serviço e não via este form. */
  mode: 'create' | 'edit';
  /** Hooks de estrutura — presentes só no modo create. */
  onSubmitStructure?: (state: BaseComercialFormState) => Promise<void> | void;
}

/**
 * Formulário multi-abas da Base Comercial (tasks 10.3 e 10.4).
 * Componentes controlados (`useState`), sem `react-hook-form` — padrão
 * do projeto. Abas viram telas separadas em mobile (<768px), como no
 * `OpportunityForm`.
 */
export const BaseComercialForm: React.FC<BaseComercialFormProps> = ({
  initialState,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting,
  submitError,
  mode,
}) => {
  const [state, setState] = useState<BaseComercialFormState>(initialState ?? EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<TabId>('identificacao');

  useEffect(() => {
    if (initialState) setState(initialState);
  }, [initialState]);

  const set = <K extends keyof BaseComercialFormState>(
    k: K,
    v: BaseComercialFormState[K],
  ) => {
    setState((s) => ({ ...s, [k]: v }));
    setErrors((e) => {
      const next = { ...e };
      // Sempre limpa o próprio campo — o usuário está editando ele.
      if (next[k as string]) next[k as string] = '';
      // Data de início e data final estão amarradas (fim >= início). Se
      // o usuário alterar uma, o erro na outra deixou de ser confiável e
      // precisa ser recalculado no submit — limpa também aqui.
      if (k === 'dataInicio' && next.dataFim) next.dataFim = '';
      if (k === 'dataFim' && next.dataFim) next.dataFim = '';
      return next;
    });
  };

  const { data: servicosDominio } = useDominio('servicos');
  const { data: perfisDominio } = useDominio('perfis');
  const { data: precificacaoDominio } = useDominio('precificacao');
  const { data: indicesDominio } = useDominio('indices');

  const { data: clientesResp } = useQuery({
    queryKey: ['picker', 'clientes'],
    queryFn: () => listarClientes({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const { data: usuariosResp } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });

  const clienteOptions = useMemo(
    () =>
      (clientesResp?.data ?? [])
        .filter((c) => c.situacaoCadastral === 'ativo')
        .map((c) => ({
          value: c.id,
          label: c.razaoSocial,
          description: `${c.code} • ${c.documento}`,
        })),
    [clientesResp],
  );

  const usuarioOptions = useMemo(
    () =>
      (usuariosResp?.data ?? []).map((u) => ({
        value: u.id,
        label: u.name,
        description: u.email,
      })),
    [usuariosResp],
  );

  const servicoOptions = useMemo(
    () =>
      (servicosDominio ?? []).map((s) => ({ value: s.slug, label: s.nome })),
    [servicosDominio],
  );
  const perfilOptions = useMemo(
    () => (perfisDominio ?? []).map((p) => ({ value: p.slug, label: p.nome })),
    [perfisDominio],
  );
  const precificacaoOptions = useMemo(
    () =>
      (precificacaoDominio ?? []).map((p) => ({ value: p.slug, label: p.nome })),
    [precificacaoDominio],
  );
  const indiceOptions = useMemo(
    () =>
      (indicesDominio ?? []).map((i) => ({ value: i.slug, label: i.nome })),
    [indicesDominio],
  );

  // Estrutura — task 10.4 --------------------------------------------
  // Toda mutação da estrutura (add/update/remove serviço ou item) limpa
  // `errors.structure` — sem isso, o erro "Adicione ao menos um serviço"
  // ou "Valor unitário obrigatório" persistia até o próximo submit,
  // mesmo depois do usuário corrigir o campo.
  const clearStructureError = () =>
    setErrors((e) => (e.structure ? { ...e, structure: '' } : e));

  const addServico = () => {
    clearStructureError();
    setState((s) => ({
      ...s,
      servicos: [
        ...s.servicos,
        { localId: makeLocalId(), serviceTypeSlug: '', descricao: '', itens: [] },
      ],
    }));
  };
  const removeServico = (localId: string) => {
    clearStructureError();
    setState((s) => ({ ...s, servicos: s.servicos.filter((x) => x.localId !== localId) }));
  };
  const updateServico = (localId: string, patch: Partial<BaseComercialFormState['servicos'][number]>) => {
    clearStructureError();
    setState((s) => ({
      ...s,
      servicos: s.servicos.map((x) => (x.localId === localId ? { ...x, ...patch } : x)),
    }));
  };
  const addItem = (servicoLocalId: string) => {
    clearStructureError();
    setState((s) => ({
      ...s,
      servicos: s.servicos.map((sv) =>
        sv.localId === servicoLocalId
          ? {
              ...sv,
              itens: [
                ...sv.itens,
                {
                  localId: makeLocalId(),
                  professionalProfileSlug: '',
                  pricingModelSlug: '',
                  quantidade: '1',
                  valorUnitario: '',
                  custoUnitario: '',
                  vigenciaInicio: '',
                  vigenciaFim: '',
                  observacoes: '',
                },
              ],
            }
          : sv,
      ),
    }));
  };
  const removeItem = (servicoLocalId: string, itemLocalId: string) => {
    clearStructureError();
    setState((s) => ({
      ...s,
      servicos: s.servicos.map((sv) =>
        sv.localId === servicoLocalId
          ? { ...sv, itens: sv.itens.filter((i) => i.localId !== itemLocalId) }
          : sv,
      ),
    }));
  };
  const updateItem = (
    servicoLocalId: string,
    itemLocalId: string,
    patch: Partial<BaseComercialFormState['servicos'][number]['itens'][number]>,
  ) => {
    clearStructureError();
    setState((s) => ({
      ...s,
      servicos: s.servicos.map((sv) =>
        sv.localId === servicoLocalId
          ? {
              ...sv,
              itens: sv.itens.map((i) => (i.localId === itemLocalId ? { ...i, ...patch } : i)),
            }
          : sv,
      ),
    }));
  };

  const handleSubmit = () => {
    const v = validateForm(state);
    const vs = validateStructure(state);
    const all = { ...v, ...vs };
    setErrors(all);
    if (Object.keys(all).length > 0) {
      if (all.nome || all.clientId || all.responsavelComercialId || all.dataInicio || all.modalidade || all.dataFim) {
        setActiveTab('identificacao');
      } else if (all.structure) {
        setActiveTab('estrutura');
      } else if (all.reajusteIndexSlug) {
        setActiveTab('reajuste');
      }
      return;
    }
    void onSubmit(state);
  };

  // Totais recalculados na tela a partir da estrutura (task 10.4).
  const { totalValor, totalCusto } = useMemo(() => {
    let valor = 0;
    let custo = 0;
    for (const s of state.servicos) {
      for (const it of s.itens) {
        const q = Number(it.quantidade) || 0;
        const v = Number(it.valorUnitario) || 0;
        const c = Number(it.custoUnitario) || 0;
        valor += q * v;
        if (it.custoUnitario !== '' && Number.isFinite(Number(it.custoUnitario))) {
          custo += q * c;
        }
      }
    }
    return { totalValor: valor, totalCusto: custo };
  }, [state.servicos]);

  const currentTabIndex = TABS.findIndex((t) => t.id === activeTab);
  const goPrevTab = () => {
    if (currentTabIndex > 0) setActiveTab(TABS[currentTabIndex - 1].id);
  };
  /**
   * Valida só os campos da aba atual e, se ok, avança. Assim o wizard
   * força o usuário a corrigir erros antes de passar adiante, em vez de
   * acumular tudo pro submit final.
   */
  const goNextTab = () => {
    if (currentTabIndex >= TABS.length - 1) return;
    const tabErrors = validateTabErrors(activeTab, state);
    if (Object.keys(tabErrors).length > 0) {
      // Sobrescreve apenas as chaves da aba atual — preserva erros de
      // outras abas que já foram validadas.
      setErrors((e) => ({ ...e, ...tabErrors }));
      return;
    }
    setActiveTab(TABS[currentTabIndex + 1].id);
  };
  const isFirstTab = currentTabIndex === 0;
  const isLastTab = currentTabIndex === TABS.length - 1;
  const currentTab = TABS[currentTabIndex];

  return (
    <div className="space-y-4">
      {/* Step indicator mobile */}
      <div className="md:hidden">
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
              {currentTab.icon}
              {currentTab.label}
            </div>
            <span className="text-xs font-medium text-gray-500">
              Etapa {currentTabIndex + 1} de {TABS.length}
            </span>
          </div>
          <div className="mt-2 h-1 w-full rounded-full bg-gray-200">
            <div
              className="h-1 rounded-full bg-blue-600 transition-all"
              style={{ width: `${((currentTabIndex + 1) / TABS.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Tabs desktop */}
      <div className="hidden border-b border-gray-200 md:block">
        <nav className="-mb-px flex gap-4" aria-label="Abas da Base Comercial">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`inline-flex items-center gap-1 border-b-2 px-1 py-2 text-sm font-medium ${
                activeTab === t.id
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
              aria-current={activeTab === t.id ? 'page' : undefined}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </nav>
      </div>

      {submitError && (
        <Alert variant="error">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{submitError}</span>
          </div>
        </Alert>
      )}

      {/* Aba: Identificação */}
      {activeTab === 'identificacao' && (
        <div className="space-y-4">
          <TextInput
            label="Nome da Base Comercial"
            value={state.nome}
            onChange={(e) => set('nome', e.target.value)}
            error={errors.nome}
          />
          <AutocompleteSelect
            label="Cliente (ativo)"
            options={clienteOptions}
            value={state.clientId}
            onChange={(v) => set('clientId', v)}
            error={errors.clientId}
            disabled={mode === 'edit'}
          />
          {mode === 'edit' && (
            <p className="text-xs text-gray-500">
              Cliente não pode ser alterado — trocar o cliente é criar outra Base.
            </p>
          )}
          <AutocompleteSelect
            label="Responsável comercial"
            options={usuarioOptions}
            value={state.responsavelComercialId}
            onChange={(v) => set('responsavelComercialId', v)}
            error={errors.responsavelComercialId}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput
              label="Data de início"
              type="date"
              value={state.dataInicio}
              onChange={(e) => set('dataInicio', e.target.value)}
              error={errors.dataInicio}
            />
            <TextInput
              label="Data final (vazia = indeterminada)"
              type="date"
              value={state.dataFim}
              onChange={(e) => set('dataFim', e.target.value)}
              error={errors.dataFim}
            />
          </div>
          <TextInput
            label="Modalidade"
            value={state.modalidade}
            onChange={(e) => set('modalidade', e.target.value)}
            error={errors.modalidade}
          />
          <div>
            <Textarea
              label="Escopo (opcional)"
              rows={3}
              value={state.escopo}
              onChange={(e) => set('escopo', e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              Delimita a Base dentro de um recorte específico (ex.: filial, produto,
              torre de serviço). Quando preenchido, entra na unicidade — pode haver
              outra Base vigente para o mesmo cliente/modalidade se o escopo for
              diferente (RN-COM-002).
            </p>
          </div>
        </div>
      )}

      {/* Aba: Estrutura */}
      {activeTab === 'estrutura' && (
        <div className="space-y-4">
          {errors.structure && (
            <Alert variant="error">{errors.structure}</Alert>
          )}
          {state.servicos.length === 0 && (
            <p className="text-sm text-gray-500">
              Nenhum serviço adicionado. Adicione um serviço (ex.: Outsourcing) e seus
              perfis com a forma de precificação (RN-COM-008/009/010).
            </p>
          )}
          {state.servicos.map((sv) => (
            <div key={sv.localId} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[200px] flex-1">
                  <AutocompleteSelect
                    label="Tipo de serviço"
                    options={servicoOptions}
                    value={sv.serviceTypeSlug}
                    onChange={(v) => updateServico(sv.localId, { serviceTypeSlug: v })}
                  />
                </div>
                <div className="min-w-[200px] flex-1">
                  <TextInput
                    label="Descrição (opcional)"
                    value={sv.descricao}
                    onChange={(e) => updateServico(sv.localId, { descricao: e.target.value })}
                  />
                </div>
                <Button
                  variant="ghost"
                  onClick={() => removeServico(sv.localId)}
                  aria-label="Remover serviço"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  Remover
                </Button>
              </div>

              {/* Antes: <table overflow-x-auto> com cada item numa <tr>.
                  Trocado por cards empilhados porque o overflow-x criava
                  scroll context que clipava o dropdown do AutocompleteSelect
                  (Perfil/Precificação) e escondia opções. Cards deixam o
                  Combobox.Options extrapolar livremente e ainda melhoram
                  a leitura em mobile (labels ficam sempre acima do input). */}
              <div className="mt-3 space-y-3">
                {sv.itens.length === 0 && (
                  <p className="text-xs text-gray-500">
                    Adicione um perfil para este serviço.
                  </p>
                )}
                {sv.itens.map((it, idx) => (
                  <div
                    key={it.localId}
                    className="rounded-md border border-gray-200 bg-white p-3 shadow-sm"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                        Perfil #{idx + 1}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(sv.localId, it.localId)}
                        aria-label="Remover perfil"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                        Remover
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <AutocompleteSelect
                        label="Perfil"
                        options={perfilOptions}
                        value={it.professionalProfileSlug}
                        onChange={(v) =>
                          updateItem(sv.localId, it.localId, { professionalProfileSlug: v })
                        }
                      />
                      <AutocompleteSelect
                        label="Precificação"
                        options={precificacaoOptions}
                        value={it.pricingModelSlug}
                        onChange={(v) =>
                          updateItem(sv.localId, it.localId, { pricingModelSlug: v })
                        }
                      />
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <TextInput
                        label="Qtd"
                        type="number"
                        min={0}
                        value={it.quantidade}
                        onChange={(e) =>
                          updateItem(sv.localId, it.localId, { quantidade: e.target.value })
                        }
                      />
                      <TextInput
                        label="Valor unit."
                        type="number"
                        min={0}
                        value={it.valorUnitario}
                        onChange={(e) =>
                          updateItem(sv.localId, it.localId, { valorUnitario: e.target.value })
                        }
                      />
                      <TextInput
                        label="Custo unit."
                        type="number"
                        min={0}
                        value={it.custoUnitario}
                        onChange={(e) =>
                          updateItem(sv.localId, it.localId, { custoUnitario: e.target.value })
                        }
                      />
                    </div>
                    <div className="mt-3">
                      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                        Vigência própria (opcional)
                      </p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <TextInput
                          label="Início"
                          type="date"
                          value={it.vigenciaInicio}
                          onChange={(e) =>
                            updateItem(sv.localId, it.localId, {
                              vigenciaInicio: e.target.value,
                            })
                          }
                        />
                        <TextInput
                          label="Fim"
                          type="date"
                          value={it.vigenciaFim}
                          onChange={(e) =>
                            updateItem(sv.localId, it.localId, { vigenciaFim: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                variant="secondary"
                size="sm"
                onClick={() => addItem(sv.localId)}
                className="mt-2"
              >
                <Plus className="h-3.5 w-3.5" />
                Adicionar perfil
              </Button>
            </div>
          ))}

          <Button variant="secondary" onClick={addServico}>
            <Plus className="h-4 w-4" />
            Adicionar serviço
          </Button>
        </div>
      )}

      {/* Aba: Precificação */}
      {activeTab === 'precificacao' && (
        <div className="space-y-4">
          <AutocompleteSelect
            label="Moeda"
            options={MOEDA_OPTIONS}
            value={state.moeda}
            onChange={(v) => set('moeda', v as MoedaBaseComercial)}
          />
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Totais estimados (calculados na tela — RN-COM-017)
            </div>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <div className="text-xs text-gray-500">Valor total</div>
                <div className="text-lg font-semibold tabular-nums text-gray-900">
                  {totalValor.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: state.moeda,
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Receita estimada</div>
                <div className="text-lg font-semibold tabular-nums text-gray-900">
                  {totalValor.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: state.moeda,
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Margem estimada</div>
                <div className="text-lg font-semibold tabular-nums text-gray-900">
                  {totalCusto > 0
                    ? `${(((totalValor - totalCusto) / totalValor) * 100).toFixed(2)}%`
                    : '—'}
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Os valores oficiais são congelados pelo backend no momento do salvamento.
              A margem só é calculada quando todos os itens têm custo (RN-COM-017).
            </p>
          </div>
        </div>
      )}

      {/* Aba: Reajuste e Descontos */}
      {activeTab === 'reajuste' && (
        <div className="space-y-4">
          <AutocompleteSelect
            label="Periodicidade de reajuste"
            options={REAJUSTE_PERIODICIDADE_OPTIONS}
            value={state.reajustePeriodicidade}
            onChange={(v) => set('reajustePeriodicidade', v as ReajustePeriodicidade)}
          />
          {state.reajustePeriodicidade !== 'nenhuma' && (
            <AutocompleteSelect
              label="Índice"
              options={indiceOptions}
              value={state.reajusteIndexSlug}
              onChange={(v) => set('reajusteIndexSlug', v)}
              error={errors.reajusteIndexSlug}
            />
          )}
          {state.reajusteIndexSlug === 'fixo' && (
            <TextInput
              label="Percentual fixo do reajuste"
              type="number"
              value={state.reajustePercentualFixo}
              onChange={(e) => set('reajustePercentualFixo', e.target.value)}
            />
          )}
          <TextInput
            label="Desconto máximo (%) — opcional"
            type="number"
            min={0}
            value={state.descontoMaximoPercentual}
            onChange={(e) => set('descontoMaximoPercentual', e.target.value)}
            error={errors.descontoMaximoPercentual}
          />
          <div className="flex flex-col gap-2 text-sm">
            <label className="flex items-center gap-2 text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={state.permiteCoexistencia}
                onChange={(e) => set('permiteCoexistencia', e.target.checked)}
              />
              Permitir coexistência (exceção à unicidade de Base vigente)
            </label>
            <label className="flex items-center gap-2 text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={state.requerAprovacaoEletronica}
                onChange={(e) => set('requerAprovacaoEletronica', e.target.checked)}
              />
              Exigir aprovação eletrônica para publicar
            </label>
          </div>
          <Textarea
            label="Condições de faturamento (opcional)"
            rows={3}
            value={state.condicoesFaturamento}
            onChange={(e) => set('condicoesFaturamento', e.target.value)}
          />
          <Textarea
            label="Observações tributárias (opcional)"
            rows={3}
            value={state.observacoesTributarias}
            onChange={(e) => set('observacoesTributarias', e.target.value)}
          />
          <Textarea
            label="Observações (opcional)"
            rows={3}
            value={state.observacoes}
            onChange={(e) => set('observacoes', e.target.value)}
          />
        </div>
      )}

      {/* Footer: wizard — "Continuar" avança abas com validação, o
          submit final ("Criar/Salvar") só aparece na última aba. Isso
          força o usuário a corrigir erros da aba corrente antes de
          seguir, em vez de acumular tudo pro submit e voltar. */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <div className="flex items-center gap-2">
            {!isFirstTab && (
              <Button variant="secondary" onClick={goPrevTab}>
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                Anterior
              </Button>
            )}
            {isLastTab ? (
              <Button onClick={handleSubmit} loading={isSubmitting}>
                {submitLabel}
              </Button>
            ) : (
              <Button onClick={goNextTab}>
                Continuar
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'identificacao', label: 'Identificação', icon: <Info className="h-4 w-4" /> },
  { id: 'estrutura', label: 'Serviços e Perfis', icon: <Layers className="h-4 w-4" /> },
  { id: 'precificacao', label: 'Precificação', icon: <DollarSign className="h-4 w-4" /> },
  { id: 'reajuste', label: 'Reajuste e Descontos', icon: <TrendingUp className="h-4 w-4" /> },
];

export { EMPTY_STATE as BASE_COMERCIAL_FORM_EMPTY_STATE };
